'use client';

import * as React from 'react';
import { Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface InfoIconDialogProps {
  /** Title shown in the dialog header */
  title: string;
  /** Content shown in the dialog body (explainer text) */
  children: React.ReactNode;
  /** Optional class for the trigger button */
  className?: string;
  /** Accessible label for the icon button */
  ariaLabel?: string;
}

/**
 * Renders an info icon that opens a dialog with title and content on click.
 * Use to keep the UI clean while making explainer text available on demand.
 */
export function InfoIconDialog({
  title,
  children,
  className,
  ariaLabel = 'More information',
}: InfoIconDialogProps) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors p-0.5',
            className
          )}
          aria-label={ariaLabel}
        >
          <Info className="h-4 w-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-muted-foreground whitespace-pre-line">{children}</div>
      </DialogContent>
    </Dialog>
  );
}
